import b from 'b_';
import { HeartEmpty, HeartFilled } from 'education-icons';
import { Text, useAppContext } from 'platform-components';
import { UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/src/components/constants';
import { getMe } from 'platform-components/src/components/user/selectors';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ym from 'react-yandex-metrika';
import { accessibleClick, accessibleMouseHover } from '@edu-frontend/modules/src/shared/lib/a11y';

import './lessons__lesson-likes.scss';

interface Props {
    isLarge?: boolean;
    clessonId: number;
    likesCount: number | undefined;
    onMouseEvent?: (target: HTMLElement, enter: boolean) => void;
}

export const LessonLikes = React.memo((props: Props) => {
    const { clessonId, likesCount, onMouseEvent, isLarge } = props;
    const me = useSelector(getMe);
    const likeRef = useRef<HTMLDivElement>(null);
    const {
        uatraits: { isTouch },
    } = useAppContext();
    const handleTouch = (event: React.MouseEvent<HTMLElement>) => {
        if (isTouch) {
            event.stopPropagation();
            event.preventDefault();
            if (onMouseEvent) {
                onMouseEvent(event.currentTarget, true);
            }
        }
    };

    const handleMouseEvent = (event: React.MouseEvent<HTMLElement>, enter: boolean) => {
        if (onMouseEvent) {
            onMouseEvent(event.currentTarget, enter);
        }
    };

    useEffect(() => {
        if (typeof likesCount === 'number') {
            ym('reachGoal', 'teacher_lesson_view_like_counter', {
                teacher_id: me?.id,
                cl_id: clessonId,
                like_count: likesCount,
            });
        }
    }, [clessonId, likesCount, me?.id]);

    const withCounter = !!likesCount && likesCount > 1;

    const className = b('lessons', 'lesson-likes', { large: isLarge });
    const typo = isLarge ? UI_KIT_TEXT_TYPOGRAPHY.paragraphMMedium : UI_KIT_TEXT_TYPOGRAPHY.paragraphSMedium;

    return (
        <div
            className={className}
            {...accessibleClick(handleTouch)}
            {...accessibleMouseHover({
                onMouseEnter: (e: React.MouseEvent<HTMLElement>) => handleMouseEvent(e, true),
                onMouseLeave: (e: React.MouseEvent<HTMLElement>) => handleMouseEvent(e, false),
                describedby: false,
            })}
            ref={likeRef}
        >
            {likesCount ? (
                <HeartFilled className={b('lessons', 'lesson-likes-icon', { filled: true })} />
            ) : (
                <HeartEmpty className={b('lessons', 'lesson-likes-icon', { empty: true })} />
            )}
            {withCounter && (
                <Text className="lessons__lesson-likes-count" typography={typo}>
                    {likesCount}
                </Text>
            )}
        </div>
    );
});

export default LessonLikes;
